import DownloadIcon from "@mui/icons-material/Download";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Button,
  Container,
  Divider,
  Drawer,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import emailIcon from "../../../assets/icons/emailIcon.svg";
import playStoreIcon from "../../../assets/icons/playStoreIcon.svg";
import AstrologyLogo from "../../../assets/icons/xiegaLogo.svg";
import urls from "../../../global/constants/UrlConstants";
import { selectAuthenticated } from "../../../redux/authSlice";
import history from "../../../utils/history";
import { useAppSelector } from "../../../utils/hooks";
import ContactUsDialog from "../Dialogs/ContactUsDialog/ContactUsDialog";
import appFooterStyles from "./AppFooter.styles";

const AppFooter = () => {
  const classes = appFooterStyles;
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectAuthenticated);
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const muiTheme = useTheme();
  const isMobileOrTablet = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [footerExpanded, setFooterExpanded] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const getYear = () => {
    return new Date().getFullYear();
  };

  const handleDownload = async (platform: "android" | "ios") => {
    const language = localStorage.getItem("userLanguage") || "en";
    const fileName = `${platform}_guide_${language}.pdf`;
    const fileUrl = `${process.env.PUBLIC_URL}/guides/${fileName}`;

    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const getAstrologyLogo = () => {
    return (
      <Box sx={{ cursor: "pointer", textAlign: { md: "start", xs: "center" } }}>
        <a href={isAuthenticated ? urls.dashboardViewPath : urls.homeViewPath}>
          <img src={AstrologyLogo} alt="Module Not Found..." height={"100px"} />
        </a>
      </Box>
    );
  };

  const getLinks = () => {
    return (
      <Box sx={{ display: "grid", justifyContent: "center" }}>
        <Box sx={classes.headBox}>
          <Typography sx={classes.headTypo}>{t("quickLinks")}</Typography>
        </Box>
        <Stack
          direction="column"
          spacing={1}
          pt={2}
          alignItems={{ md: "start", xs: "center" }}
        >
          <Typography
            sx={classes.linkTypo}
            onClick={() => {
              isAuthenticated
                ? history.push(urls.dashboardViewPath)
                : history.push(urls.homeViewPath);
            }}
          >
            {t("footerHome")}
          </Typography>
          {isAuthenticated && (
            <Typography sx={classes.linkTypo} onClick={() => setOpen(true)}>
              {t("contactUs")}
            </Typography>
          )}
          <Typography
            sx={classes.linkTypo}
            onClick={() => history.push(urls.privacyPolicyViewPath)}
          >
            {t("privacyPolicyFooter")}
          </Typography>
          <Typography
            sx={classes.linkTypo}
            onClick={() => history.push(urls.aboutUsViewPath)}
          >
            {t("aboutUs")}
          </Typography>
          <Box
            pt={1}
            onClick={() =>
              window.open(
                "https://play.google.com/store/apps/details?id=com.akariaryaca.xiegawodnika",
              )
            }
            sx={{ cursor: "pointer" }}
          >
            <img src={playStoreIcon} />
          </Box>
        </Stack>
      </Box>
    );
  };

  const getContactInfo = () => {
    return (
      <Box sx={{ display: "grid", justifyContent: "center" }}>
        <Box sx={classes.headBox}>
          <Typography sx={classes.headTypo}>{t("footerContact")}</Typography>
        </Box>
        <Stack
          direction="column"
          spacing={1}
          pt={2}
          alignItems={{ md: "start", xs: "center" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <img src={emailIcon} />
            <Typography sx={classes.contentTypo}>:</Typography>
            <a
              href="mailto:info@xiegawodnika.com"
              style={{ textDecoration: "auto" }}
            >
              <Typography sx={classes.linkTypo}>
                info@xiegawodnika.com
              </Typography>
            </a>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <LocationOnIcon
              sx={{
                color: "#FFFFFF",
                fontSize: "20px",
                flexShrink: 0,
                mt: "2px",
              }}
            />
            <Typography sx={classes.contentTypo}>:</Typography>
            <Typography
              sx={{ ...classes.contentTypo, textTransform: "capitalize" }}
            >
              Noyras Limited, Ealing Cross 1st Floor, 85 Uxbridge Road, London,
              United Kingdom, W5 5TH, NIP: PL5263135088
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const getPWASetupGuide = () => {
    return (
      <Box sx={{ display: "grid", justifyContent: "center" }}>
        <Stack direction={"column"} spacing={1}>
          <Box sx={classes.headBox}>
            <Typography sx={classes.headTypo}>{t("pwaSetupGuide")}</Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            pt={2}
            alignItems={{ md: "start", xs: "center" }}
          >
            <Button
              sx={classes.whiteBtnStyle}
              onClick={() => {
                handleDownload("android");
              }}
              startIcon={<DownloadIcon />}
            >
              {t("android")}
            </Button>
            <Button
              sx={classes.whiteBtnStyle}
              onClick={() => {
                handleDownload("ios");
              }}
              startIcon={<DownloadIcon />}
            >
              {t("ios")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const getFooterContent = () => {
    return (
      <Grid container pb={2} spacing={2}>
        <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
          {getAstrologyLogo()}
          <Typography sx={classes.contentTypo} pt={2}>
            {t("homeContent")}
          </Typography>
        </Grid>
        <Grid item xl={3} lg={2.5} md={6} sm={12} xs={12}>
          {getLinks()}
        </Grid>
        <Grid item xl={3} lg={2.5} md={6} sm={12} xs={12}>
          {getContactInfo()}
        </Grid>
        <Grid item xl={3} lg={4} md={6} sm={12} xs={12}>
          {getPWASetupGuide()}
        </Grid>
      </Grid>
    );
  };

  const getCopyrights = () => {
    return (
      <Box pt={2}>
        <Box display={"flex"} justifyContent={"center"}>
          <Typography sx={classes.footerTypo}>
            &copy; {getYear()} {t("footer")}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getAppFooter = () => {
    if (isMobileOrTablet) {
      return (
        <>
          {/* Fixed floating trigger button */}
          <Box
            sx={classes.floatingFooterTrigger}
            onClick={() => setFooterExpanded((prev) => !prev)}
            role="button"
            aria-label={footerExpanded ? "Hide footer" : "Show footer"}
          >
            <ExpandLessIcon
              sx={{
                fontSize: "22px",
                color: "#fff",
                transform: footerExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </Box>

          {/* Bottom sheet drawer */}
          <Drawer
            anchor="bottom"
            open={footerExpanded}
            onClose={() => setFooterExpanded(false)}
            PaperProps={{ sx: classes.footerDrawerPaper }}
          >
            {/* Drag handle — tap to close */}
            <Box
              sx={classes.drawerHandle}
              onClick={() => setFooterExpanded(false)}
            >
              <Box sx={classes.drawerHandleBar} />
            </Box>
            <Container maxWidth="xl" sx={{ pb: 3 }}>
              {getFooterContent()}
              <Divider sx={{ border: "1px solid #ffffff59" }} />
              {getCopyrights()}
            </Container>
          </Drawer>

          <ContactUsDialog
            setIsLoading={setIsLoading}
            setOpenContactUs={setOpen}
            handleClose={handleClose}
            openContactUs={open}
          />
        </>
      );
    }

    return (
      <Box sx={classes.footer}>
        <Container maxWidth="xl">
          {getFooterContent()}
          <Divider sx={{ border: "1px solid #ffffff59" }} />
          {getCopyrights()}
          <ContactUsDialog
            setIsLoading={setIsLoading}
            setOpenContactUs={setOpen}
            handleClose={handleClose}
            openContactUs={open}
          />
        </Container>
      </Box>
    );
  };

  return getAppFooter();
};

export default AppFooter;
